import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { productImages } from "../data/images";

const TILE_KEYS = ["parquet", "vinyl", "laminate", "carpet", "design"];

export const ProductCategories = ({ hideHeader = false }) => {
  const { t } = useLang();
  const items = t("products.items") || [];

  return (
    <section id="products" className="relative py-24 lg:py-32 bg-[#F9F8F6]" data-testid="products-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {!hideHeader && (
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5"
              data-testid="products-eyebrow"
            >
              — {t("products.eyebrow")}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[0.95]"
              data-testid="products-title"
            >
              {t("products.title")}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-base text-[#4A4A4A] leading-relaxed max-w-md"
            data-testid="products-subtitle"
          >
            {t("products.subtitle")}
          </motion.p>
        </div>
        )}

        {/* Bento grid: 6 cols x 4 rows on lg */}
        <div className="grid grid-cols-2 lg:grid-cols-6 lg:grid-rows-4 gap-3 lg:gap-4 lg:h-[760px]">
          {items.map((it, i) => {
            const layout = [
              "col-span-2 lg:col-span-3 lg:row-span-3",   // Parquet - large
              "col-span-1 lg:col-span-3 lg:row-span-2",   // Vinyl - top right
              "col-span-1 lg:col-span-3 lg:row-span-1",   // Laminate - middle right
              "col-span-2 lg:col-span-3 lg:row-span-1",   // Carpet - bottom left
              "col-span-2 lg:col-span-3 lg:row-span-1",   // Design floor - bottom right
            ];
            const key = TILE_KEYS[i] || "parquet";
            return (
              <motion.div
                key={key + i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative zoom-wrap ${layout[i]} min-h-[200px] lg:min-h-0 bg-[#EFEBE6]`}
                data-testid={`product-tile-${key}`}
              >
                <Link to="/contact" className="absolute inset-0 block">
                  <img
                    src={productImages[key]}
                    alt={it.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                  <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-end text-white">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] opacity-75">0{i + 1}</div>
                        <div className="font-display text-2xl lg:text-3xl mt-1">{it.name}</div>
                      </div>
                      <ArrowUpRight
                        size={22}
                        strokeWidth={1.4}
                        className="opacity-80 transform transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                    <p className="text-sm opacity-85 mt-3 max-w-md leading-relaxed">{it.desc}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
